<template>
  <b-modal
    :id="'aicSynchronizationModal'"
    ref="aicSynchronizationModal"
    :title="$t('message.aicSynchronization.title')"
    size="lg"
    no-close-on-backdrop
    hide-footer>
    <div class="aic-synchronization-modal">
      <div class="mb-3">
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t('message.aicSynchronization.description') }}</span>
          <coozzy-info-circle-icon
            v-b-tooltip="{
              html: true,
              title: tooltipContent,
              container: 'body'
            }" />
        </div>
      </div>

      <div class="properties-table-container">
        <b-table
          :items="properties"
          :fields="tableFields"
          small
          responsive
          class="properties-table">
          <template #head(propertyName)>
            <span>{{ $t('message.aicSynchronization.columns.propertyName') }}</span>
          </template>
          <template #head(propertyId)>
            <span>{{ $t('message.aicSynchronization.columns.propertyId') }}</span>
          </template>
          <template #head(supportOption)>
            <span />
          </template>
          <template #cell(propertyName)="data">
            <span>{{ data.item.name || '-' }}</span>
          </template>

          <template #cell(propertyId)="data">
            <span>{{ data.item.numericId | displayOptionalValue }}</span>
          </template>

          <template #cell(supportOption)="data">
            <div class="d-flex align-items-center justify-content-around">
              <coozzy-form-radio
                :id="`with-support-${data.item.objectID}`"
                v-model="data.item.aicWithSupport"
                :select-value="true"
                :name="`with-support-${data.item.objectID}`"
                class="mr-3"
                :disabled="isPropertySyncedWithSupport(data.item)"
                :default-model="data.item.aicWithSupport">
                {{ $t('message.aicSynchronization.options.withSupport') }}
              </coozzy-form-radio>
              <coozzy-form-radio
                :id="`without-support-${data.item.objectID}`"
                v-model="data.item.aicWithSupport"
                :select-value="false"
                :name="`with-support-${data.item.objectID}`"
                :disabled="isPropertySyncedWithSupport(data.item)"
                :default-model="data.item.aicWithSupport">
                {{ $t('message.aicSynchronization.options.withoutSupport') }}
              </coozzy-form-radio>
            </div>
          </template>
        </b-table>
      </div>

      <div class="modal-footer-actions">
        <coozzy-button
          design="transparent"
          class="mr-2 border"
          @click="cancel">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          design="green"
          @click="confirm">
          {{ $t('message.generic.confirm') }}
        </coozzy-button>
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts">
import Vue from 'vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import { AicAicSyncMode } from '@/types/api'
import { PropertyRecord } from '@/types/search/PropertyRecord'
import { tables } from '@/mixins/tables'

export default Vue.extend({
  name: 'AicSynchronizationModal',
  components: {
    CoozzyInfoCircleIcon,
    CoozzyFormRadio,
    CoozzyButton
  },
  mixins: [tables],
  props: {
    entries: {
      type: Array,
      default: () => []
    },
    accountId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      properties: [] as PropertyRecord[],
      tableFields: [
        {
          key: 'propertyName'
        },
        {
          key: 'propertyId'
        },
        {
          key: 'supportOption'
        }
      ]
    }
  },
  computed: {
    tooltipContent() {
      return `
        <div class="aic-tooltip-content">
          <div class="mb-3">
            <h6 class="font-weight-bold">${this.$t('message.aicSynchronization.options.withSupport')}</h6>
            <p>${this.$t('message.aicSynchronization.tooltip.withSupport.description')}</p>
          </div>
          <div class="mb-4">
            <p>${this.$t('message.aicSynchronization.tooltip.withSupport.prerequisite')}</p>
          </div>
          <div>
            <h6 class="font-weight-bold">${this.$t('message.aicSynchronization.options.withoutSupport')}</h6>
            <p>${this.$t('message.aicSynchronization.tooltip.withoutSupport.description')}</p>
          </div>
        </div>
      `
    }
  },
  watch: {
    entries(newVal) {
      this.loadProperties()
    }
  },
  mounted() {
    this.loadProperties()
  },
  methods: {
    show() {
      this.$refs.aicSynchronizationModal.show()
    },
    hide() {
      this.$refs.aicSynchronizationModal.hide()
    },
    cancel() {
      this.hide()
      this.$emit('cancel')
    },
    confirm() {
      this.hide()
      this.$emit('confirm', this.properties)
    },
    isPropertySyncedWithSupport(property: PropertyRecord) {
      if (!property.aicSyncMode || !this.accountId) {
        return false
      }

      const syncMode = property.aicSyncMode[this.accountId.toString()] as AicAicSyncMode
      return [
        AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_NOT_CONFIRMED,
        AicAicSyncMode.AIC_SYNC_MODE_ENABLED_WITH_SUPPORT_CONFIRMED
      ].includes(syncMode)
    },
    loadProperties() {
      this.properties = this.entries.map(entry => ({
        ...entry,
        aicWithSupport: true
      }))
    }
  }
})
</script>

<style lang="scss" scoped>
.aic-synchronization-modal {
  .properties-table-container {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
  }

  .properties-table {
    margin-bottom: 0;

    :deep(.table) {
      margin-bottom: 0;
    }

    :deep(tbody tr) {
      border-bottom: 1px solid #dee2e6;
    }

    :deep(tbody tr:last-child) {
      border-bottom: none;
    }
  }

  .modal-footer-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
  }

  :deep(.form-check-input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :deep(.form-check-label:has(.form-check-input:disabled)) {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

:deep(.aic-tooltip-content) {
  text-align: left;
  padding: 10px;
}

:deep(.tooltip) {
  max-width: 400px !important;
}

:deep(.tooltip-inner) {
  max-width: 400px !important;
}
</style>
